/**
 * useCalendarRangeData / useCalendarUserMeta — 캘린더 데이터 레이어 공용 훅
 *
 * 채널 캘린더(CalendarInlineView)와 메인 캘린더(CalendarProvider)가 공유하는
 * SWR(stale-while-revalidate) + TTL 메모리 캐시. 데이터 페칭 정책을 이 파일
 * 한 곳에서만 관리한다 (한쪽만 고쳐지고 다른 쪽이 누락되는 사고 방지).
 *
 * 동작:
 *  - 캐시가 있으면 즉시 표시 → TTL(30s) 이내면 네트워크 요청 자체를 생략,
 *    경과 시 캐시를 보여준 채 백그라운드 재검증 후 "지금 보고 있는 범위일 때만" 반영.
 *  - refresh()(일정 생성/수정/삭제 후)는 해당 사용자의 캐시를 전부 무효화 —
 *    채널/메인이 같은 일정을 서로 다른 스코프 키로 캐싱하므로 교차 무효화가 안전.
 *  - 사용자 단위 메타(참석 event_id, 리마인더 맵)는 범위와 무관 →
 *    useCalendarUserMeta 로 분리. 모듈 캐시 + in-flight 공유로 채널/메인이
 *    동시에 마운트돼 있어도 중복 요청이 나가지 않는다.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { CalendarEvent } from '../../models/CalendarEvent';
import { DateRange } from '../../context/CalendarContext';
import { fetchMyAttendingEventIds, fetchMyRemindersMap } from '../../services/CalendarService';
import { My } from '../../services/My';

// ── 범위 데이터 캐시 ─────────────────────────────────────────────
const RANGE_CACHE_TTL_MS = 30_000;

export interface CalendarRangeData {
  events: CalendarEvent[];
  taskEvents: CalendarEvent[];
}

interface RangeCacheEntry extends CalendarRangeData {
  fetchedAt: number;
}

const rangeCache = new Map<string, RangeCacheEntry>();

function setRangeCache(key: string, entry: RangeCacheEntry) {
  rangeCache.delete(key); // 재삽입으로 삽입 순서를 최신화 → 아래 cap 이 오래된 것부터 제거
  rangeCache.set(key, entry);
  if (rangeCache.size > 100) {
    const oldest = rangeCache.keys().next().value;
    if (oldest) rangeCache.delete(oldest);
  }
}

/** 사용자의 모든 스코프(채널/메인) 캐시 무효화 — 뮤테이션 후 호출 */
function invalidateUserRangeCache(userId: string) {
  const prefix = `${userId}|`;
  for (const key of Array.from(rangeCache.keys())) {
    if (key.startsWith(prefix)) rangeCache.delete(key);
  }
}

export interface UseCalendarRangeDataOptions {
  /**
   * 범위 외의 fetch 파라미터를 모두 캡처하는 스코프 키.
   * 예: `channel|${workspaceId}|${roomId}`, `main|${workspaceId}|${channelIds.join(',')}`
   * fetchRange 의 결과에 영향을 주는 값이 바뀌면 이 키도 바뀌어야 한다.
   */
  scopeKey: string;
  range: DateRange;
  /** false 면 로드하지 않음 (예: 메인 캘린더는 캘린더 모듈 활성일 때만) */
  enabled: boolean;
  /** 범위 데이터 fetcher. 최신 클로저를 ref 로 보관하므로 의존성 안정성은 신경쓰지 않아도 됨 */
  fetchRange: (range: DateRange) => Promise<CalendarRangeData>;
  /** fetch 실패 시 err.message 가 없을 때 표시할 문구 (호출측에서 번역해 전달) */
  errorFallback?: string;
}

export function useCalendarRangeData({ scopeKey, range, enabled, fetchRange, errorFallback }: UseCalendarRangeDataOptions) {
  const [events, setEvents] = useState<CalendarEvent[]>([]);
  const [taskEvents, setTaskEvents] = useState<CalendarEvent[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchRangeRef = useRef(fetchRange);
  fetchRangeRef.current = fetchRange;
  const errorFallbackRef = useRef(errorFallback);
  errorFallbackRef.current = errorFallback;

  // 현재 보고 있는 범위의 캐시 키 — 늦게 도착한 응답이 화면을 덮지 않도록 판별
  const cacheKey = `${My.user.id}|${scopeKey}|${range.start.getTime()}|${range.end.getTime()}`;
  const cacheKeyRef = useRef(cacheKey);
  cacheKeyRef.current = cacheKey;

  const load = useCallback(async (force = false) => {
    if (!enabled) return;
    const key = `${My.user.id}|${scopeKey}|${range.start.getTime()}|${range.end.getTime()}`;
    const cached = rangeCache.get(key);
    if (cached) {
      // 캐시 즉시 표시 (재방문 체감 0ms)
      setEvents(cached.events);
      setTaskEvents(cached.taskEvents);
      setError(null);
    }
    // TTL 이내 재방문은 네트워크 요청 자체를 생략
    if (!force && cached && Date.now() - cached.fetchedAt < RANGE_CACHE_TTL_MS) return;
    try {
      if (!cached) setLoading(true); // 보여줄 캐시가 없을 때만 로딩 상태 (있으면 조용히 재검증)
      setError(null);
      const data = await fetchRangeRef.current(range);
      setRangeCache(key, { ...data, fetchedAt: Date.now() });
      // 응답 대기 중 다른 달/스코프로 이동했을 수 있음 → 현재 키일 때만 화면 반영
      if (cacheKeyRef.current === key) {
        setEvents(data.events);
        setTaskEvents(data.taskEvents);
      }
    } catch (err: any) {
      console.error('[useCalendarRangeData] 일정 로드 실패:', err);
      if (cacheKeyRef.current === key) setError(err?.message ?? errorFallbackRef.current ?? 'failed');
    } finally {
      if (!cached) setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, scopeKey, range.start.getTime(), range.end.getTime()]);

  // 일정 생성/수정/삭제 후 강제 갱신 — 다른 달/다른 캘린더에 영향 가는 변경도 있으므로 사용자 캐시 전체 무효화
  const refresh = useCallback(async () => {
    if (My.user.id) invalidateUserRangeCache(My.user.id);
    await load(true);
  }, [load]);

  useEffect(() => { load(); }, [load]);

  return { events, setEvents, taskEvents, setTaskEvents, loading, setLoading, error, refresh };
}

// ── 사용자 단위 메타 (참석 event_id, 리마인더 맵) ────────────────
// 범위와 무관 → 세션 내 1회 + 뮤테이션 후에만 갱신. 채널/메인 동시 마운트 시 in-flight 공유.
const USER_META_TTL_MS = 30_000;

interface UserMeta {
  attendingEventIds: string[];
  remindersMap: Record<string, number[]>;
  fetchedAt: number;
}

let userMetaCache: { userId: string; data: UserMeta } | null = null;
let userMetaInflight: { userId: string; promise: Promise<UserMeta> } | null = null;

async function loadUserMeta(userId: string, force: boolean): Promise<UserMeta> {
  if (!force && userMetaCache?.userId === userId && Date.now() - userMetaCache.data.fetchedAt < USER_META_TTL_MS) {
    return userMetaCache.data;
  }
  if (!force && userMetaInflight?.userId === userId) return userMetaInflight.promise;
  const promise = (async () => {
    const [attendingEventIds, remindersMap] = await Promise.all([
      fetchMyAttendingEventIds(userId).catch(() => [] as string[]),
      fetchMyRemindersMap(userId).catch(() => ({} as Record<string, number[]>)),
    ]);
    const data: UserMeta = { attendingEventIds, remindersMap, fetchedAt: Date.now() };
    userMetaCache = { userId, data };
    return data;
  })();
  userMetaInflight = { userId, promise };
  promise.finally(() => { if (userMetaInflight?.promise === promise) userMetaInflight = null; });
  return promise;
}

export function useCalendarUserMeta(enabled = true) {
  const [attendingEventIds, setAttendingEventIds] = useState<Set<string>>(new Set());
  const [remindersMap, setRemindersMap] = useState<Record<string, number[]>>({});

  const apply = useCallback((meta: UserMeta) => {
    setAttendingEventIds(new Set(meta.attendingEventIds));
    setRemindersMap(meta.remindersMap);
  }, []);

  /** 뮤테이션(참석 응답/리마인더 변경 등) 후 강제 갱신 */
  const refreshUserMeta = useCallback(async () => {
    if (!My.user.id) return;
    apply(await loadUserMeta(My.user.id, true));
  }, [apply]);

  useEffect(() => {
    if (enabled && My.user.id) {
      loadUserMeta(My.user.id, false).then(apply).catch(() => {});
    }
  }, [enabled, apply]);

  return { attendingEventIds, remindersMap, refreshUserMeta };
}
